import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { SiDiscord } from "react-icons/si";
import { FiExternalLink } from "react-icons/fi";
import "./About.css";

function animateCount(el, target, suffix, duration, format = true) {
  let startTime = null;
  let rafId;
  const step = (ts) => {
    if (!startTime) startTime = ts;
    const progress = Math.min((ts - startTime) / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    const val = Math.round(eased * target);
    el.textContent = (format ? val.toLocaleString() : val) + suffix;
    if (progress < 1) { rafId = requestAnimationFrame(step); }
  };
  rafId = requestAnimationFrame(step);
  return () => cancelAnimationFrame(rafId);
}

const About = () => {
  const headlineRef = useRef(null);
  const membersRef = useRef(null);
  const foundedRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (headlineRef.current) {
        headlineRef.current.style.transform = `translateY(${window.scrollY * 0.12}px)`;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const el = headlineRef.current;
    if (!el) return;

    const line1 = 'ZOMBS';
    const line2 = 'FIGHT-FI.';
    const speed = 80;

    // Pre-render every character as invisible — locks h1 to final two-line height immediately
    const makeSpan = (ch) => {
      const s = document.createElement('span');
      s.textContent = ch;
      s.style.opacity = '0';
      return s;
    };

    const line1Spans = Array.from(line1, makeSpan);
    const line2Spans = Array.from(line2, makeSpan);
    const br = document.createElement('br');
    const accent = document.createElement('span');
    accent.className = 'hero-title-accent';

    el.innerHTML = '';
    line1Spans.forEach(s => el.appendChild(s));
    el.appendChild(br);
    line2Spans.forEach(s => accent.appendChild(s));
    el.appendChild(accent);

    const allSpans = [...line1Spans, ...line2Spans];
    let i = 0;
    let timerId;

    const type = () => {
      if (i < allSpans.length) {
        allSpans[i++].style.opacity = '1';
        timerId = setTimeout(type, speed);
      } else {
        const content = el.closest('.hero-content');
        if (content) content.classList.add('revealed');
        timerId = setTimeout(() => {
          if (membersRef.current) animateCount(membersRef.current, 7500, '+', 1800);
          if (foundedRef.current) animateCount(foundedRef.current, 2020, '', 1500, false);
        }, 850);
      }
    };

    timerId = setTimeout(type, 450);
    return () => clearTimeout(timerId);
  }, []);

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-grid" aria-hidden="true" />
        <div className="hero-star hero-star-0" aria-hidden="true" />
        <div className="hero-star hero-star-1" aria-hidden="true" />
        <div className="hero-star hero-star-2" aria-hidden="true" />
        <div className="hero-star hero-star-3" aria-hidden="true" />
        <div className="hero-star hero-star-4" aria-hidden="true" />
        <div className="hero-star hero-star-5" aria-hidden="true" />
        <div className="hero-star hero-star-6" aria-hidden="true" />
        <div className="hero-star hero-star-7" aria-hidden="true" />
        <div className="hero-comet hero-comet-0" aria-hidden="true" />
        <div className="hero-comet hero-comet-1" aria-hidden="true" />
        <div className="hero-comet hero-comet-2" aria-hidden="true" />
        <div className="hero-comet hero-comet-3" aria-hidden="true" />
        <div className="hero-comet hero-comet-4" aria-hidden="true" />
        <div className="hero-fade" aria-hidden="true" />

        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title font-display" ref={headlineRef} />

            <p className="hero-subtitle">
              The ultimate competitive platform for ZombsRoyale.
              Daily tournaments, prize pools, verified clan challenges.
            </p>

            <div className="hero-actions">
              <a
                href="https://discord.com/invite/98T6NeC4vQ"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-hero-primary"
              >
                <SiDiscord size={16} />
                <span>Join Discord</span>
                <span className="btn-arrow">→</span>
              </a>
              <Link to="/tournaments" className="btn-hero-ghost">
                Browse Tournaments <span className="btn-arrow">→</span>
              </Link>
            </div>

            <div className="hero-stats">
              <div className="hero-stat">
                <span className="hero-stat-value" style={{ position: 'relative' }}>
                  <span style={{ visibility: 'hidden' }} aria-hidden="true">7,500+</span>
                  <span ref={membersRef} style={{ position: 'absolute', left: 0, top: 0 }}>0+</span>
                </span>
                <span className="hero-stat-label">Members</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="hero-stat-value">Daily</span>
                <span className="hero-stat-label">Events</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="hero-stat-value" style={{ position: 'relative' }}>
                  <span style={{ visibility: 'hidden' }} aria-hidden="true">2020</span>
                  <span ref={foundedRef} style={{ position: 'absolute', left: 0, top: 0 }}>0</span>
                </span>
                <span className="hero-stat-label">Founded</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose ZFT?</h2>
            <p className="section-subtitle">The premier destination for competitive ZombsRoyale</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <span className="feature-num">01</span>
              <h3 className="feature-title">Daily Tournaments</h3>
              <p className="feature-description">
                Compete in exciting tournaments almost every day. From casual
                matches to intense competitive events with prize pools.
              </p>
            </div>

            <div className="feature-card">
              <span className="feature-num">02</span>
              <h3 className="feature-title">Thriving Community</h3>
              <p className="feature-description">
                Join over 7,500 active members in our Discord community.
                Founded in February 2020, we're the home of competitive ZombsRoyale.
              </p>
            </div>

            <div className="feature-card">
              <span className="feature-num">03</span>
              <h3 className="feature-title">All Game Modes</h3>
              <p className="feature-description">
                From solos to squads, custom lobbies to special events.
                We cater to all game modes and players from every region.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-content">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Who We Are</h2>
            <p className="section-subtitle">Building the future of the ZombsRoyale community</p>
          </div>
          <div className="content-grid">
            <div className="content-card">
              <h3 className="card-title">Our Mission</h3>
              <p className="section-text">
                Welcome to Zombs Fight-Fi Tournaments! ZFT is a ZombsRoyale-oriented
                events &amp; tournaments server, hosting the largest and most competitive
                events in the community.
              </p>
              <p className="section-text">
                Our events are competitive yet fun, with special prize-pool tournaments
                that bring out the best in every player. Win events and get your clan
                verified through our exciting challenges.
              </p>
            </div>

            <div className="content-card">
              <h3 className="card-title">Community First</h3>
              <p className="section-text">
                Whether you're a seasoned pro or brand-new to competitive play,
                you'll find squads, scrims, and friendly rivals waiting for you.
              </p>
              <p className="section-text">
                We use custom lobbies for our flagship events, host regular tournaments,
                and maintain an active community that keeps the spirit of competition alive.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="about-footer">
        <div className="container">
          <div className="footer-inner">
            <div className="footer-brand">
              <span className="footer-logo font-display">ZFT</span>
              <p className="footer-tagline">Zombs Fight-Fi Tournaments</p>
            </div>

            <div className="footer-links">
              <div className="footer-col">
                <span className="footer-col-label">Navigate</span>
                <Link to="/tournaments" className="footer-link">Tournaments</Link>
                <Link to="/fight-club" className="footer-link">Fight Club</Link>
                <Link to="/zombs-royale" className="footer-link">ZombsRoyale</Link>
              </div>
              <div className="footer-col">
                <span className="footer-col-label">Community</span>
                <a
                  href="https://discord.com/invite/98T6NeC4vQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  Discord <FiExternalLink size={11} />
                </a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <span className="footer-copy">&copy; {new Date().getFullYear()} Zombs Fight-Fi Tournaments. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
